import Link from 'next/link';
import Image from 'next/image';
import { Box, Typography } from '@/primitives';
import { formatDistanceToNow } from 'date-fns';
import { lineClampSx } from '@/utils/text';
import { slate } from '@/lib/colours';

/**
 * Squarespace serves images at whatever `?format=<n>w` width is requested.
 * The RSS feed gives us `?format=1500w` for every image even though the
 * brief cards display them at ~96px and main cards at ~500px. Rewriting the
 * format param ships a much smaller source to the Next optimizer, which
 * then re-encodes to AVIF/WebP at the actual rendered width.
 */
const SQUARESPACE_CDN_HOST = 'squarespace-cdn.com';

function withSquarespaceWidth(url: string | undefined, width: number) {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    const isSquarespaceHost =
      parsed.hostname === SQUARESPACE_CDN_HOST ||
      parsed.hostname.endsWith(`.${SQUARESPACE_CDN_HOST}`);
    if (!isSquarespaceHost) return url;
    parsed.searchParams.set('format', `${width}w`);
    return parsed.toString();
  } catch {
    return url;
  }
}

export type NewsCardProps = {
  date?: Date;
  imgUrl?: string;
  isBrief?: boolean;
  /** Dark landing section styling */
  landing?: boolean;
  referenceLink: string;
  snippet: string;
  title: string;
};

export default function NewsCard({
  date,
  imgUrl,
  isBrief = false,
  landing = false,
  referenceLink,
  snippet,
  title,
}: NewsCardProps) {
  const timeAgo = date ? formatDistanceToNow(date, { addSuffix: true }) : '';
  const formattedTime = timeAgo.replace(
    /^about |^over |^less than |^almost /,
    '',
  );

  const titleColor = landing ? slate[100] : slate[900];
  const snippetColor = landing ? slate[400] : slate[600];
  const timeColor = landing ? slate[500] : slate[500];

  const resolvedImgUrl = withSquarespaceWidth(imgUrl, isBrief ? 300 : 500);

  return (
    <Link href={referenceLink} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isBrief ? 'row' : 'column',
          borderRadius: landing ? '14px' : 0,
          maxWidth: { xs: 'unset', md: '378px' },
          backgroundColor: landing ? slate[900] : 'transparent',
          border: landing ? `1px solid ${slate[800]}` : 'none',
          overflow: 'hidden',
          transition: landing
            ? 'border-color 0.2s, box-shadow 0.2s'
            : undefined,
          '&:hover': landing
            ? {
                borderColor: slate[600],
                boxShadow: `0 12px 40px -20px rgba(0,0,0,0.45)`,
              }
            : undefined,
        }}
      >
        {resolvedImgUrl && (
          // Aspect-ratio wrapper: `next/image` with `fill` requires a
          // positioned ancestor with a defined size.
          <Box
            sx={{
              position: 'relative',
              borderRadius: landing ? 0 : isBrief ? 0 : '12px',
              overflow: 'hidden',
              flexShrink: 0,
              height: {
                sm: isBrief ? '96px' : 'unset',
              },
              width: {
                sm: isBrief ? '96px' : '100%',
              },
              aspectRatio: isBrief
                ? '1 / 1'
                : {
                    xs: '350 / 300',
                    md: '336 / 260',
                  },
            }}
          >
            <Image
              // Decorative: the article title is rendered as text beside the
              // image inside the same link, so a non-empty alt would make
              // screen readers announce the headline twice.
              alt=""
              src={resolvedImgUrl}
              fill
              loading="lazy"
              sizes={
                isBrief
                  ? '96px'
                  : '(max-width: 600px) 350px, (max-width: 900px) 336px, 378px'
              }
              style={{ objectFit: 'cover' }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: isBrief
              ? landing
                ? '16px 20px !important'
                : '0 16px !important'
              : landing
                ? '20px !important'
                : '12px 0 !important',
          }}
        >
          <Typography
            color={titleColor}
            variant={isBrief ? 'titlesmall' : 'titlemedium'}
          >
            {title}
          </Typography>
          <Typography
            color={snippetColor}
            sx={{
              ...lineClampSx,
              mt: isBrief ? 0 : '4px',
              lineHeight: isBrief ? '24px' : '20px',
            }}
            variant="bodymedium"
          >
            {snippet}
          </Typography>
          <Typography
            color={timeColor}
            sx={{ mt: isBrief ? 0 : '8px' }}
            variant="bodymedium"
          >
            {formattedTime}
          </Typography>
        </Box>
      </Box>
    </Link>
  );
}
